import { Component } from 'react'
import { connect } from 'react-redux'
import NotificationSystem from 'react-notification-system'
import { withRouter, withSettings } from 'hocs'
import get from 'lodash/get'
import isEqual from 'lodash/isEqual'
import { HeaderContainer } from './Header'
import {
  isAuthenticated,
  getDateFormat,
  getImpersonationToken,
  isAdmin,
  isImpersonating,
  setToken,
  removeImpersonationData
} from 'utils/authService'
import ReactLoading from 'react-loading'
import s from './Layout.scss'
import { URLS } from 'utils/constants'
import { toPageTitle } from './serializers'
import { ErrorBoundary } from 'components/ideal-core-ui'
import { Zendesk } from 'components/Zendesk'
import moment from 'moment'
import { getPaymentOverdueDate } from 'utils/paymentService'
import cn from 'classnames'
import { Link } from 'react-router-dom'
import { timezoneDate } from 'utils/helpers'
import { ImpersonationBanner } from './ImpersonationBanner'
import { makePostRequest } from 'utils/api'

const style = {
  NotificationItem: {
    DefaultStyle: {
      top: '50px'
    }
  }
}

const loginOrRegistrationRoutes = [URLS.ORGANIZATION_REGISTRATION, URLS.LOGIN]

const checkRoute = (route, pathname) => {
  const reg = new RegExp(route.split(':')[0] + '[0-9]+')
  return route === pathname || reg.test(pathname)
}

const createLoadingSelector = actions => {
  // returns true only when all actions are not loading
  return Object.keys(actions).some(property => get(actions, property))
}

@connect(({ app, loading, login, settings }) => {
  const { notifications, pageTitle, pageDescription } = app
  const { subscriptions, hasValidCreditCard, hasExpiredCreditCard } = login
  const { status, plan, trialEndDate } = subscriptions

  return {
    notifications,
    isFetching: loading.isFetching || createLoadingSelector(loading),
    pageTitle,
    pageDescription,
    hasValidCreditCard,
    hasExpiredCreditCard,
    subscriptionStatus: status,
    subscriptionPlan: plan,
    trialEndDate,
    settings: settings.settings
  }
})
@withRouter
class LayoutContainer extends Component {
  state = {
    showPaymentWarning: true,
    showExpiredCardWarning: true,
    showSetEmailWarning: false,
    showImpersonationBanner: false
  }

  notificationRef = el => {
    this.notificationSystem = el
  }

  pushNotification = () => {
    this.notificationSystem.addNotification({
      title: this.props.notifications.title,
      message: this.props.notifications.message,
      level: this.props.notifications.level,
      position: this.props.notifications.position,
      autoDismiss: this.props.notifications.autoDismiss,
      dismissible: this.props.notifications.dismissible,
      uid: this.props.notifications.uid,
      onRemove: this.props.notifications.onRemove
    })
  }

  componentDidUpdate(prevProps) {
    if (get(this.props.notifications, 'clear', false)) {
      const { uid } = this.props.notifications
      this.notificationSystem.removeNotification(uid)
    } else if (prevProps.notifications !== this.props.notifications) {
      this.pushNotification()
    }

    if (!isEqual(this.props.pageTitle, prevProps.pageTitle)) {
      document.title = toPageTitle(this.props.pageTitle)
    }
    if (!isEqual(this.props.pageDescription, prevProps.pageDescription)) {
      document.getElementsByTagName('meta')['description'].content = this.props.pageDescription
    }

    if (this.props.settings?.emailIntegration !== prevProps.settings?.emailIntegration) {
      this.setState({
        showSetEmailWarning: this.props.settings.emailIntegration === 'NON_PAYING'
      })
    }
  }

  componentDidMount() {
    // Check impersonation status on mount
    this.setState({
      showImpersonationBanner: isImpersonating()
    })
  }

  handleExitImpersonation = async () => {
    // The server closes the session but returns no token: an impersonation token must never be
    // exchangeable for an admin session. The admin token saved at impersonation start is the way back.
    const adminToken = getImpersonationToken()
    try {
      await makePostRequest('/v1/users/exit-impersonation')
    } catch (error) {
      // Close the session server-side, but never strand the admin in impersonation over it.
      console.error('Exit impersonation failed:', error)
    }

    removeImpersonationData()

    if (adminToken) {
      setToken(adminToken)
    }

    this.setState({ showImpersonationBanner: false })

    window.location.href = URLS.AUTH_HOME
  }

  render() {
    const {
      children,
      isFetching,
      router,
      subscriptionStatus,
      subscriptionPlan,
      hasValidCreditCard,
      hasExpiredCreditCard,
      trialEndDate
    } = this.props

    const { pathname } = router.location
    const isLoginOrRegistration = loginOrRegistrationRoutes.some(route => {
      return checkRoute(route, pathname)
    })
    const isLogin = checkRoute(URLS.LOGIN, pathname)

    const isAuth = isAuthenticated()

    const paymentDate = getPaymentOverdueDate()
    const untilDate = paymentDate
      ? moment(timezoneDate(paymentDate)).add(2, 'weeks').format(getDateFormat())
      : moment(timezoneDate(trialEndDate)).add(30, 'days').format(getDateFormat())

    const showPaymentWarning =
      isAuth &&
      isAdmin() &&
      this.state.showPaymentWarning &&
      (paymentDate ||
        (subscriptionStatus === 'active' &&
          ['SMALL_COMPANY', 'ENTERPRISE'].includes(subscriptionPlan) &&
          !hasValidCreditCard))

    const showExpiredCreditCardWarning =
      isAuth &&
      isAdmin() &&
      this.state.showExpiredCardWarning &&
      hasExpiredCreditCard &&
      hasValidCreditCard

    return (
      <>
        <div
          className={cn(s.content, {
            [s.hasPaymentWarning]: showPaymentWarning,
            [s.loginPage]: isLogin
          })}
        >
          {isAuth && <HeaderContainer className={s.headerContainer} />}
          {this.state.showImpersonationBanner && isAuth && (
            <ImpersonationBanner onExitImpersonation={this.handleExitImpersonation} />
          )}
          {showPaymentWarning && (
            <div
              onClick={() => {
                this.setState({
                  showPaymentWarning: false
                })
              }}
              className={s.paymentWarning}
            >
              <span>
                iDeal CRM is free for your entire organization for an unlimited amount of{' '}
                <Link to="/admin?menu=users&tab=organization">users</Link> until{' '}
                {timezoneDate(trialEndDate)}. Please enter a valid credit card on your{' '}
                <Link to="/admin?menu=subscription&tab=organization">Subscription</Link> page
                {paymentDate
                  ? 'to continue enjoying iDeal CRM.'
                  : hasValidCreditCard
                    ? ` before ${untilDate} to continue enjoying iDeal CRM.`
                    : 'to continue enjoying iDeal CRM.'}
              </span>
              <div
                className={s.close}
                onClick={() => {
                  this.setState({
                    showPaymentWarning: false
                  })
                }}
              >
                x
              </div>
            </div>
          )}
          {showExpiredCreditCardWarning && (
            <div
              onClick={() => {
                this.setState({
                  showExpiredCardWarning: false
                })
              }}
              className={s.paymentWarning}
            >
              <span>
                Your credit card appears to be expired. Please enter a valid credit card on your{' '}
                <Link to="/admin?menu=subscription&tab=organization">Subscription</Link> page to
                continue enjoying iDeal CRM.
              </span>
              <div
                className={s.close}
                onClick={() => {
                  this.setState({
                    showExpiredCardWarning: false
                  })
                }}
              >
                x
              </div>
            </div>
          )}
          {this.state.showSetEmailWarning && (
            <div
              onClick={() => {
                this.setState({
                  showSetEmailWarning: false
                })
              }}
              className={s.paymentWarning}
            >
              <span>
                With a free account, in order to send emails you need to configure SMTP or Outlook
                {isAdmin() ? (
                  <Link to="/admin?menu=email&tab=organization"> here</Link>
                ) : (
                  '. Please contact your system admin'
                )}
                .
              </span>
              <div
                className={s.close}
                onClick={() => {
                  this.setState({
                    showSetEmailWarning: false
                  })
                }}
              >
                x
              </div>
            </div>
          )}
          <ErrorBoundary>
            <div
              style={
                isLoginOrRegistration
                  ? { width: '100%' }
                  : {
                      paddingTop: this.state.showImpersonationBanner
                        ? showPaymentWarning
                          ? '140px'
                          : '100px'
                        : showPaymentWarning
                          ? '100px'
                          : '60px',
                      height: '100%'
                    }
              }
            >
              {children}
            </div>
          </ErrorBoundary>
        </div>
        <NotificationSystem ref={this.notificationRef} style={style} />
        {isFetching && (
          <div className={s.loadingDiv}>
            <ReactLoading type="spinningBubbles" />
          </div>
        )}
        {process.env.ENV === 'production' && <Zendesk isVisible={isAuth} />}
      </>
    )
  }
}

export default withSettings(LayoutContainer)
